import { Chip, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useUserPrivilegeKeyPermissionsMap } from "../../hooks/use_backend";
import { useSiteData } from "../../hooks/use_site";
import InfoCard from "../general/info_card";
import CreateIcon from "@mui/icons-material/Create";
import AddIcon from "@mui/icons-material/Add";
import { useRenameSiteDialog } from "../dialogs/rename_site_dialog";
import { useAddSiteOwnerDialog } from "../dialogs/add_site_owner_dialog";
import { hasPermission, isComponentDisabledForUpdates } from "../../pages/login/login_utils";
import Privilege from "../../enums/privilege_enum";
import { toLocaleDateString, toLocaleString } from "../../scripts/utils";
import { get_status_for_action } from "../../pages/elion_site/tabs/commissioning_tab/components/enum/commissioning_required_action";

export function GeneralInfoLine({ name, value, width }) {
  return (
    <Stack direction="row" sx={{ width: width ? width : "100%" }}>
      <Typography
        variant="body"
        sx={{
          minWidth: "130px",
          display: "flex",
          userSelect: "none",
          fontWeight: "bold",
          color: "#999",
        }}
      >
        {name}
      </Typography>
      <Typography variant="body" sx={{ wordWrap: "break-word", fontWeight: "bold", display: "inline-block" }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function GeneralInfoCard({ required_privilege, tooltip }) {
  const { data: siteData } = useSiteData();

  const buildAddress = (type) =>
    siteData["ean_" + type + "_street"] +
    " " +
    siteData["ean_" + type + "_street_number"] +
    ", " +
    siteData["ean_" + type + "_city"].split(" ")[0];

  const contractType =
    siteData.has_active_offtake && siteData.has_active_injection
      ? "OFFTAKE, INJECTION"
      : siteData.has_active_offtake
        ? "OFFTAKE"
        : siteData.has_active_injection
          ? "INJECTION"
          : null;

  const commissioning = siteData.commissioning_completed_date_utc
    ? toLocaleString(siteData.commissioning_completed_date_utc)
    : get_status_for_action(siteData.commissioning_required_action);

  const openRenameSiteDialog = useRenameSiteDialog();
  const openAddOwnersDialog = useAddSiteOwnerDialog();

  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const canEdit = !isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey());

  return (
    <InfoCard
      title={"General info"}
      tooltip={tooltip}
      content={[
        { key: "Site ID", value: siteData.site_id },
        {
          key: "Status",
          value: (
            <Chip
              variant="outlined"
              size="small"
              label={siteData.is_active ? "Active" : "Inactive"}
              color={siteData.is_active ? "success" : "error"}
            />
          ),
        },
        {
          key: "Site Name",
          value: siteData.site_name,
          buttonIcon: canEdit ? <CreateIcon /> : null,
          onButtonClick: () => openRenameSiteDialog(siteData),
        },
        {
          key: "Installers",
          value: siteData.owners,
          buttonIcon: canEdit ? <AddIcon /> : null,
          onButtonClick: () => openAddOwnersDialog(siteData),
        },
        { key: "Contract", value: contractType },
        {
          key: "Address",
          value: siteData["ean_offtake_street"]
            ? buildAddress("offtake")
            : siteData["ean_injection_street"]
              ? buildAddress("injection")
              : null,
        },
        { key: "Commissioning", value: commissioning },
      ]}
    />
  );
}
